import React, {useState} from 'react'
import {Formik, type FormikHelpers} from 'formik'
import {
  Button,
  ButtonGroup,
  FormControlWrapper,
  FormLayout,
  Icon,
  Label,
  Modal,
  Tooltip,
} from '@therms/atalaya'
import {
  Alerts,
  DateTime,
  FormikDateTimePicker,
  FormikTextarea,
  RPCDocs,
  useRPCContext,
  useSessionStorage,
} from '@therms/react-modules'
import {pluralize} from '@therms/web-js'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import {SelectLogEntryAndFormModal} from '@src/modules/Locations/shared/SelectLogEntryAndFormModal'
import {Timer} from '@src/components/Timer'
import classNames from 'classnames'
import {getDepartureTimeBasedOnArrivalTime} from '@src/modules/Patrol/router/screens/components/PatrolRouteRunModal/components/PatrolRunVisitFormModal/utils/getDepartureTimeBasedOnArrivalTime'
import {IoIosListBox} from 'react-icons/io'
import {FaExclamationTriangle} from 'react-icons/fa'
import {MdKeyboardDoubleArrowDown, MdOpenInBrowser} from 'react-icons/md'
import {ViewLocationModal} from '@src/modules/Locations/shared/ViewLocationModal.tsx'
import NewStatus = RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.NewStatus
import Status = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.VisitsStatus

const ValidationSchema = Yup.object().shape({
  arrivalTime: Yup.string(),
  departureTime: Yup.string(),
  notes: Yup.string(),
  status: Yup.string(),
})
export type FormValues = {
  arrivalTime: string
  departureTime: string
  status?: string
  notes: string
}

const initialValues = {
  arrivalTime: '',
  departureTime: '',
  notes: '',
  status: undefined,
}

interface PatrolRunVisitFormModalProps {
  arrivalTime?: string
  closeHandler: () => void
  departureTime?: string
  lengthMin?: number
  location?: RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Location
  locationId: string
  notes?: string
  onComplete?: () => void
  runVisitId: string
  status?: Status
  visitNotes?: string
}

function PatrolRunVisitFormModal({
  arrivalTime,
  closeHandler,
  departureTime,
  lengthMin,
  location,
  locationId,
  notes,
  onComplete,
  runVisitId,
  status,
  visitNotes,
}: PatrolRunVisitFormModalProps) {
  const {rpcService} = useRPCContext()
  const [isLogEntryModalOpen, setIsLogEntryModalOpen] = useState(false)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [logEntryCreationTimestamps, setLogEntryCreationTimestamps] =
    useSessionStorage<string[]>(
      `visit_form_log_entry_creation_timestamps_${runVisitId}`,
      [],
    )

  const handleSubmit = (
    values: FormValues,
    actions: FormikHelpers<FormValues>,
  ) => {
    actions.setSubmitting(true)

    const args: RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.Args = {
      arrivalTime: values.arrivalTime,
      departureTime: values.departureTime,
      id: runVisitId,
      notes: values.notes,
    }

    if (values.status) args.newStatus = values.status as NewStatus

    rpcService.Patrol.UpdatePatrolRouteRunVisit.v1
      .call(args)
      .then(() => {
        Alerts.success('Visit Saved')
        onComplete?.()
      })
      .catch((err: any) => {
        Alerts.error('There was a problem saving your visit.', err.message)

        console.log('handleVisitSave() err', err)
      })
      .finally(() => {
        actions.setFieldValue('status', undefined)
      })
  }

  const visitEndedPrematurely =
    Status.Completed &&
    dayjs(departureTime).diff(arrivalTime, 'minutes') < (lengthMin || 0)

  return (
    <Modal
      header={
        <div className="gap-x-xxs flex items-center">
          {location && (
            <>
              <Button
                onClick={() => setIsLocationModalOpen(true)}
                subtle
                variant="main"
              >
                <Icon Icon={MdOpenInBrowser} />
              </Button>

              <span className="truncate text-lg font-medium">
                {location?.name}
              </span>

              {!!location.bolos?.count && (
                <Tooltip
                  tooltip={`${location.bolos.count} ${pluralize('Bolo', location.bolos.count)}`}
                >
                  <span style={{color: location.bolos.color}}>
                    <Icon Icon={FaExclamationTriangle} />
                  </span>
                </Tooltip>
              )}

              {!!location.passDowns && (
                <Tooltip
                  tooltip={`${location.passDowns} ${pluralize('Pass Down', location.passDowns)}`}
                >
                  <Icon Icon={MdKeyboardDoubleArrowDown} />
                </Tooltip>
              )}
            </>
          )}

          <span
            className={classNames(
              'p-xxs px-xs mx-xs ml-auto shrink-0 text-sm font-bold tracking-wide',
              {
                'text-color-caution bg-caution-faded':
                  status === Status.Completed && visitEndedPrematurely,
                'text-color-main bg-main-faded': status === Status.InProgress,
                'text-color-positive bg-positive-faded':
                  status === Status.Completed && !visitEndedPrematurely,
                'text-color-strong bg-neutral-faded': status === Status.Skipped,
                'text-color-strong bg-surface-strong': !status,
              },
            )}
          >
            Visit {!status && 'Pending'}
            {status === Status.InProgress && 'In Progress'}
            {status === Status.Skipped && 'Skipped'}
            {status === Status.Completed && 'Completed'}
          </span>
        </div>
      }
      closeHandler={closeHandler}
    >
      <div className="space-y-xs mb-xl">
        <div className="gap-x-xs flex flex-wrap-reverse items-baseline">
          <span className="text-md text-color-strong font-medium">
            {lengthMin || 0} {pluralize('Minute', lengthMin || 0)}
          </span>
          <Label>Minimum Length</Label>
        </div>

        {!!visitNotes && (
          <div className="gap-x-xs flex flex-wrap">
            <Label>Notes</Label>
            <div>{visitNotes}</div>
          </div>
        )}
      </div>

      <Formik
        initialValues={{
          arrivalTime: arrivalTime || initialValues.arrivalTime,
          departureTime: departureTime || initialValues.departureTime,
          notes: notes || initialValues.notes,
        }}
        onSubmit={handleSubmit}
        validationSchema={ValidationSchema}
      >
        {({isSubmitting, setFieldValue, submitForm, values}) => {
          const handleSetQuickDepartureTime = (minutes: number) => {
            const startTime =
              values.arrivalTime || dayjs().subtract(minutes, 'm').toISOString()

            if (!values.arrivalTime) {
              setFieldValue('arrivalTime', startTime)
            }

            const endTime = dayjs(startTime).add(minutes, 'm').toISOString()
            setFieldValue('departureTime', endTime)
          }

          const visitStarted = Boolean(
            values.arrivalTime || values.departureTime,
          )

          const visitEndingPrematurely =
            !!lengthMin &&
            visitStarted &&
            dayjs(values.departureTime || undefined).diff(
              values.arrivalTime || undefined,
              'minutes',
            ) < lengthMin

          const isBetweenTwoDays =
            values.arrivalTime &&
            values.departureTime &&
            dayjs(values.arrivalTime).day() !==
              dayjs(values.departureTime).day()

          let timerAction = ''
          if (!values.arrivalTime && !values.departureTime) {
            timerAction = 'Start'
          } else if (
            (values.arrivalTime && values.departureTime) ||
            (values.arrivalTime &&
              !values.departureTime &&
              status === Status.Completed)
          ) {
            timerAction = 'Restart'
          } else if (values.arrivalTime && !values.departureTime) {
            timerAction = 'End'
          }

          return (
            <FormLayout>
              <FormControlWrapper
                label="Timer"
                subText="Automatically updates Arrival/Departure and Status when pressed."
              >
                <Button
                  outline
                  fullWidth
                  className="group relative"
                  onClick={() => {
                    const now = new Date().toISOString()

                    if (timerAction === 'Start') {
                      setFieldValue('arrivalTime', now)
                      setFieldValue('status', Status.InProgress)
                    } else if (timerAction === 'Restart') {
                      setFieldValue('arrivalTime', now)
                      setFieldValue('departureTime', undefined)
                      setFieldValue('status', Status.InProgress)
                    } else if (timerAction === 'End') {
                      setFieldValue('departureTime', now)
                      setFieldValue('status', Status.Completed)
                    }

                    submitForm()
                  }}
                >
                  <Timer
                    className="grow text-center font-mono text-lg font-bold"
                    timestampStart={
                      !status ||
                      status === Status.InProgress ||
                      values.departureTime
                        ? values.arrivalTime
                        : undefined
                    }
                    timestampEnd={values.departureTime}
                  />

                  <span className="right-sm absolute">
                    {timerAction === 'Start' && 'Start'}
                    {timerAction === 'Restart' && 'Restart'}
                    {timerAction === 'End' && 'End'}
                  </span>
                </Button>
              </FormControlWrapper>

              <FormikDateTimePicker
                disabled={isSubmitting}
                label="Arrival Time"
                mode="time"
                name="arrivalTime"
                noInitialValue
                onChangeValue={(_arrivalTime) => {
                  const newDepartureTime = getDepartureTimeBasedOnArrivalTime({
                    arrivalTime: _arrivalTime,
                    departureTime: values.departureTime,
                  })

                  setFieldValue('arrivalTime', _arrivalTime)

                  if (newDepartureTime !== values.departureTime) {
                    setFieldValue('departureTime', newDepartureTime)
                  }
                }}
                subText={
                  <div className="gap-x-xxs flex items-baseline">
                    <Button
                      onClick={() =>
                        setFieldValue('arrivalTime', new Date().toISOString())
                      }
                      size="sm"
                      outline
                    >
                      Now
                    </Button>

                    <span className="text-color-subtle text-sm">
                      set arrival time to current time.
                    </span>
                  </div>
                }
              />

              <FormikDateTimePicker
                disabled={isSubmitting}
                label="Departure Time"
                onChangeValue={(_departureTime) => {
                  setFieldValue(
                    'departureTime',
                    getDepartureTimeBasedOnArrivalTime({
                      arrivalTime: values.arrivalTime,
                      departureTime: _departureTime,
                    }),
                  )
                }}
                mode="time"
                name="departureTime"
                noInitialValue
                subText={
                  <div className="gap-xxs flex flex-wrap items-baseline">
                    <Button
                      onClick={() =>
                        setFieldValue('departureTime', new Date().toISOString())
                      }
                      size="sm"
                      outline
                    >
                      Now
                    </Button>

                    <span className="text-color-subtle text-sm">or</span>

                    <ButtonGroup size="sm" outline>
                      <Button onClick={() => handleSetQuickDepartureTime(5)}>
                        5min
                      </Button>
                      <Button onClick={() => handleSetQuickDepartureTime(10)}>
                        10min
                      </Button>
                      <Button onClick={() => handleSetQuickDepartureTime(15)}>
                        15min
                      </Button>
                      <Button onClick={() => handleSetQuickDepartureTime(30)}>
                        30min
                      </Button>
                      <Button onClick={() => handleSetQuickDepartureTime(45)}>
                        45min
                      </Button>
                      <Button onClick={() => handleSetQuickDepartureTime(60)}>
                        60min
                      </Button>
                    </ButtonGroup>

                    <span className="text-color-subtle text-sm">
                      from arrival.
                    </span>
                  </div>
                }
              />

              <FormikTextarea
                disabled={isSubmitting}
                hint={
                  <small className="text-color-light bg-caution-faded px-xxs rounded text-sm font-light">
                    Internal
                  </small>
                }
                subText={
                  <>
                    <div>
                      {logEntryCreationTimestamps.map((timestamp) => (
                        <div
                          className="bg-neutral-faded px-xs py-xxs mb-xxs flex justify-between rounded text-xs font-bold"
                          key={timestamp}
                        >
                          <span>Log Entry Created</span>
                          <DateTime timestamp={timestamp} />
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => setIsLogEntryModalOpen(true)}
                      subtle
                      size="sm"
                      variant="main"
                    >
                      <Icon Icon={IoIosListBox} size="xs" className="mr-xxs" />
                      <span className="text-xs">Create Log Entry</span>
                    </Button>
                  </>
                }
                label="Notes"
                name="notes"
              />

              {isBetweenTwoDays && (
                <div className="bg-info-faded text-color-info p-xs rounded text-center text-sm font-bold">
                  Visit ends on a different day that starts
                </div>
              )}

              <ButtonGroup className="mt-base" fullWidth>
                <Button
                  active={!status}
                  loading={isSubmitting}
                  onClick={() => {
                    setFieldValue('status', undefined)
                    submitForm()
                  }}
                  outline
                >
                  Pending
                </Button>

                <Button
                  active={status === Status.Skipped}
                  loading={isSubmitting}
                  onClick={() => {
                    setFieldValue('status', Status.Skipped)
                    submitForm()
                  }}
                >
                  Skip
                </Button>

                <Button
                  active={status === Status.InProgress}
                  loading={isSubmitting}
                  onClick={() => {
                    setFieldValue('status', Status.InProgress)
                    submitForm()
                  }}
                  variant="main"
                >
                  In Progress
                </Button>

                <Button
                  active={status === Status.Completed}
                  loading={isSubmitting}
                  onClick={() => {
                    setFieldValue('status', Status.Completed)
                    submitForm()
                  }}
                  variant={
                    !visitStarted
                      ? 'neutral'
                      : visitEndingPrematurely
                        ? 'caution'
                        : 'positive'
                  }
                >
                  Complete
                </Button>
              </ButtonGroup>
            </FormLayout>
          )
        }}
      </Formik>

      {isLogEntryModalOpen && (
        <SelectLogEntryAndFormModal
          closeHandler={() => setIsLogEntryModalOpen(false)}
          onLogEntryComplete={() => {
            setLogEntryCreationTimestamps((state) => {
              state.push(new Date().toISOString())
              return state
            })
            setIsLogEntryModalOpen(false)
          }}
          locationId={locationId}
        />
      )}

      {isLocationModalOpen && (
        <ViewLocationModal
          closeHandler={() => setIsLocationModalOpen(false)}
          locationId={locationId}
        />
      )}
    </Modal>
  )
}

export {PatrolRunVisitFormModal}
